import { midiToFrequency } from '../utils/noteUtils';

/**
 * Generate frequency for a specific note at a given reference frequency
 * @param note Note name (e.g., 'E2', 'A4')
 * @param referenceFreq Reference frequency for A4 (default: 440 Hz)
 * @returns Frequency in Hz
 */
export function getNoteFrequency(note: string, referenceFreq = 440): number {
  const noteMap: Record<string, number> = {
    'C': 0, 'C#': 1, 'Db': 1, 'D': 2, 'D#': 3, 'Eb': 3,
    'E': 4, 'F': 5, 'F#': 6, 'Gb': 6, 'G': 7, 'G#': 8,
    'Ab': 8, 'A': 9, 'A#': 10, 'Bb': 10, 'B': 11
  };

  // Parse note name and octave
  const match = note.match(/^([A-G][#b]?)(\d+)$/);
  if (!match) {
    throw new Error(`Invalid note format: ${note}`);
  }

  const [, noteName, octaveStr] = match;
  const octave = parseInt(octaveStr, 10);
  const noteNumber = noteMap[noteName];

  if (noteNumber === undefined) {
    throw new Error(`Invalid note name: ${noteName}`);
  }

  // Calculate MIDI note number (C4 = 60)
  const midiNote = (octave + 1) * 12 + noteNumber;

  return midiToFrequency(midiNote, referenceFreq);
}

/**
 * Standard frequencies at A=440 Hz
 */
export const STANDARD_FREQUENCIES = {
  // Guitar standard tuning (E A D G B E)
  GUITAR_E2: 82.41,
  GUITAR_A2: 110.00,
  GUITAR_D3: 146.83,
  GUITAR_G3: 196.00,
  GUITAR_B3: 246.94,
  GUITAR_E4: 329.63,

  // Common bass notes
  BASS_B0: 30.87,
  BASS_E1: 41.20,
  BASS_A1: 55.00,
  BASS_D2: 73.42,
  BASS_G2: 98.00,

  // Ukulele standard tuning (G C E A - soprano/concert/tenor)
  UKULELE_G4: 392.00,
  UKULELE_C4: 261.63,
  UKULELE_E4: 329.63,
  UKULELE_A4: 440.00,

  // Reference pitches
  A4_440: 440.00,
  A4_432: 432.00,
  A4_444: 444.00,
};
